'use client'

/**
 * Light/dark theme toggle for the nav chrome. A single icon button that flips between light and dark
 * via next-themes. Token-styled so it looks right in both themes. The mounted guard renders a
 * placeholder on the server (theme is unknown there) to avoid a hydration mismatch, then shows the
 * correct icon after mount - Sun when dark (click to go light), Moon when light (click to go dark).
 */
import { useEffect, useState } from 'react'
import { Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'

export default function ThemeToggle({ className = '' }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const isDark = (resolvedTheme ?? 'dark') === 'dark'
  return (
    <button
      type="button"
      aria-label="Toggle light or dark theme"
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${className}`}
    >
      {mounted
        ? (isDark ? <Sun className="h-[1.1rem] w-[1.1rem]" /> : <Moon className="h-[1.1rem] w-[1.1rem]" />)
        : <Sun className="h-[1.1rem] w-[1.1rem] opacity-0" aria-hidden />}
    </button>
  )
}
